export const ASSISTANT_ERROR_EVENT_NAME = "edms-assistant-error";

export type AssistantHttpSource =
  | "BASE_API"
  | "LOGS_API"
  | "FILEMANAGER_API"
  | "NOTIFICATION_API"
  | "HELPDESK_API";

export type AssistantErrorCode =
  | "SESSION_EXPIRED"
  | "ACCESS_DENIED_SECTION"
  | "ACCESS_DENIED_DOCUMENT"
  | "PAGE_NOT_FOUND"
  | "SERVICE_UNAVAILABLE"
  | "FILE_UPLOAD_ERROR"
  | "FILE_TOO_LARGE"
  | "FILE_OPEN_ERROR"
  | "NOTIFICATION_COUNTER_NOT_UPDATED";

export interface AssistantErrorEventDetail {
  code: AssistantErrorCode;
  source: AssistantHttpSource;
  status: number | null;
  createdAt: number;
}

const assistantErrorCodes = new Set<AssistantErrorCode>([
  "SESSION_EXPIRED",
  "ACCESS_DENIED_SECTION",
  "ACCESS_DENIED_DOCUMENT",
  "PAGE_NOT_FOUND",
  "SERVICE_UNAVAILABLE",
  "FILE_UPLOAD_ERROR",
  "FILE_TOO_LARGE",
  "FILE_OPEN_ERROR",
  "NOTIFICATION_COUNTER_NOT_UPDATED",
]);

export function notifyAssistantHttpError(
  error: any,
  source: AssistantHttpSource
) {
  if (typeof window === "undefined") {
    return;
  }

  const detail = normalizeAssistantHttpError(error, source);
  if (!detail) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<AssistantErrorEventDetail>(ASSISTANT_ERROR_EVENT_NAME, {
      detail,
    })
  );
}

export function isAssistantErrorCode(
  value: unknown
): value is AssistantErrorCode {
  return (
    typeof value === "string" &&
    assistantErrorCodes.has(value as AssistantErrorCode)
  );
}

function normalizeAssistantHttpError(
  error: any,
  source: AssistantHttpSource
): AssistantErrorEventDetail | null {
  if (isCanceledRequest(error)) {
    return null;
  }

  const config = error?.config;
  const requestPath = getRequestPath(config?.url);

  if (
    config?.silent ||
    source === "HELPDESK_API" ||
    isAssistantRequest(requestPath)
  ) {
    return null;
  }

  const status = normalizeStatus(error?.status || error?.response?.status);
  const code = resolveErrorCode(
    status,
    requestPath,
    source,
    isTimeoutRequest(error)
  );
  if (!code) {
    return null;
  }

  return {
    code,
    source,
    status,
    createdAt: Date.now(),
  };
}

function resolveErrorCode(
  status: number | null,
  requestPath: string,
  source: AssistantHttpSource,
  isTimeout: boolean
): AssistantErrorCode | null {
  if (
    source === "NOTIFICATION_API" &&
    requestPath.includes("/notification/count")
  ) {
    return "NOTIFICATION_COUNTER_NOT_UPDATED";
  }

  if (status === 401) {
    return "SESSION_EXPIRED";
  }

  if (status === 403) {
    return isDocumentRequest(requestPath)
      ? "ACCESS_DENIED_DOCUMENT"
      : "ACCESS_DENIED_SECTION";
  }

  if (source === "FILEMANAGER_API") {
    if (status === 413) {
      return "FILE_TOO_LARGE";
    }
    if (isFileDownloadRequest(requestPath)) {
      return "FILE_OPEN_ERROR";
    }
    if (isFileUploadRequest(requestPath)) {
      return "FILE_UPLOAD_ERROR";
    }
  }

  if (status === 404) {
    return "PAGE_NOT_FOUND";
  }

  if (isTimeout || (status !== null && status >= 500)) {
    return "SERVICE_UNAVAILABLE";
  }

  return null;
}

function normalizeStatus(status: unknown): number | null {
  const parsedStatus = Number(status);
  return Number.isFinite(parsedStatus) ? parsedStatus : null;
}

function isCanceledRequest(error: any): boolean {
  const code = String(error?.code || "").toUpperCase();
  const name = String(error?.name || "").toLowerCase();
  const message = String(error?.message || "").toLowerCase();

  return (
    code === "ERR_CANCELED" ||
    name === "cancelederror" ||
    name === "aborterror" ||
    message === "canceled" ||
    message === "cancelled" ||
    message.includes("aborted")
  );
}

function isTimeoutRequest(error: any): boolean {
  const code = String(error?.code || "").toUpperCase();
  const message = String(error?.message || "").toLowerCase();

  return code === "ECONNABORTED" || message.includes("timeout");
}

function getRequestPath(url: unknown): string {
  if (typeof url !== "string") {
    return "";
  }

  try {
    return new URL(url, window.location.origin).pathname.toLowerCase();
  } catch (error) {
    return url.split("?")[0].toLowerCase();
  }
}

function isAssistantRequest(requestPath: string): boolean {
  return (
    requestPath.includes("/assistant/") ||
    requestPath.includes("/helpdesk/")
  );
}

function isDocumentRequest(requestPath: string): boolean {
  return requestPath.includes("/documents/document");
}

function isFileUploadRequest(requestPath: string): boolean {
  return (
    requestPath.includes("/upload") ||
    requestPath.includes("/convert") ||
    requestPath.includes("/import")
  );
}

function isFileDownloadRequest(requestPath: string): boolean {
  return (
    requestPath.includes("/download") || requestPath.includes("/files-info")
  );
}
